import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Code, Cloud, Smartphone, Database, Shield, Settings, GraduationCap, Users, BookOpen, Target, Briefcase, UserCheck, Building, Award, ShieldCheck, Layers, QrCode } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const servicesSublinks = [
    { name: 'Corporate Training', desc: 'Industry-aligned programs', icon: GraduationCap, link: '/corporate-training' },
    { name: 'Recruitment', desc: 'IT & Non-IT talent solutions', icon: Users, link: '/recruitment' },
    { name: 'Software Development', desc: 'Custom applications', icon: Code, link: '/software-development' },
    { name: 'IT Services', desc: 'Cloud, DevOps & security', icon: Cloud, link: '/it-services' },
  ];

  const trainingSublinks = [
    { name: 'Corporate IT Training', desc: 'Customized team programs', icon: GraduationCap, link: '/corporate-training' },
    { name: 'Professional Programs', desc: 'Upskilling & certifications', icon: Award, link: '/corporate-training' },
    { name: 'Student Training', desc: 'Job-ready fresher programs', icon: BookOpen, link: '/corporate-training' },
    { name: 'Placement Assistance', desc: 'Resume & interview prep', icon: Target, link: '/corporate-training' },
  ];

  const recruitmentSublinks = [
    { name: 'IT Recruitment', desc: 'Tech talent acquisition', icon: Code, link: '/recruitment' },
    { name: 'Non-IT Recruitment', desc: 'Business & operations roles', icon: Briefcase, link: '/recruitment' },
    { name: 'Staffing Services', desc: 'Contract & permanent hiring', icon: UserCheck, link: '/recruitment' },
    { name: 'Campus Hiring', desc: 'Fresher recruitment drives', icon: Building, link: '/recruitment' },
  ];

  const softwareSublinks = [
    { name: 'Custom Software', desc: 'Tailored enterprise solutions', icon: Code, link: '/software-development' },
    { name: 'Web & Mobile Apps', desc: 'Cross-platform development', icon: Smartphone, link: '/software-development' },
    { name: 'API Integration', desc: 'Seamless system connectivity', icon: Database, link: '/software-development' },
    { name: 'Cloud Services', desc: 'AWS, Azure, GCP solutions', icon: Cloud, link: '/it-services' },
    { name: 'DevOps & SRE', desc: 'CI/CD & infrastructure', icon: Settings, link: '/it-services' },
    { name: 'Cyber Security', desc: 'Protection & compliance', icon: Shield, link: '/it-services' },
  ];

  const certificationSublinks = [
    { name: 'Our Framework', desc: 'Certification council & methodology', icon: Award, link: '/certifications/framework' },
    { name: 'Certification Levels', desc: 'AI, Data, ML & Cloud programs', icon: Layers, link: '/certifications/levels' },
    { name: 'Verify Certificate', desc: 'Authenticate a credential', icon: ShieldCheck, link: '/verify' },
    { name: 'Digital Credentials', desc: 'QR codes, IDs & security', icon: QrCode, link: '/certifications/digital-credentials' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    
    // If we're not on homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle scroll after navigation from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const DropdownMenu = ({ items, isOpen, pageLink, pageName }) => {
    if (!isOpen) return null;
    return (
      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72">
        <div className="bg-[#111827] border border-[#00E5FF]/30 rounded-xl shadow-2xl shadow-[#00E5FF]/20 overflow-hidden">
          <div className="p-2">
            {items.map((item, index) => {
              const Icon = item.icon;
              return (
                <Link
                  key={index}
                  to={item.link}
                  className="flex items-start p-3 rounded-lg hover:bg-[#00E5FF]/10 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00E5FF]/20 to-[#1DE9B6]/20 flex items-center justify-center mr-3 group-hover:from-[#00E5FF]/30 group-hover:to-[#1DE9B6]/30 transition-all">
                    <Icon className="w-4 h-4 text-[#00E5FF]" />
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm group-hover:text-[#00E5FF] transition-colors">{item.name}</div>
                    <div className="text-[#9CA3AF] text-xs">{item.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
          {pageLink && (
            <div className="border-t border-[#00E5FF]/20 p-3 bg-[#0B0F1A]/50">
              <Link to={pageLink} className="block text-center text-xs py-2 px-3 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-colors">
                View All {pageName} →
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0B0F1A]/95 backdrop-blur-lg border-b border-[#00E5FF]/20 shadow-lg shadow-[#00E5FF]/10' : 'bg-[#0B0F1A]/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer group" onClick={() => scrollToSection('hero')}>
            <img 
              src="/cloudxai-logo.png" 
              alt="CloudXAI Logo" 
              className="h-14 w-auto group-hover:scale-105 transition-transform duration-300"
              style={{ mixBlendMode: 'lighten' }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-5">
            <Link to="/" onClick={() => { if (location.pathname === '/') scrollToSection('hero'); }} className="text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
              Home
            </Link>
            <button onClick={() => scrollToSection('why-choose')} className="text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
              About Us
            </button>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('services')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
                Services
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={servicesSublinks} isOpen={activeDropdown === 'services'} />
            </div>
            
            {/* Training & Placement Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('training')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
                Training
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'training' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={trainingSublinks} isOpen={activeDropdown === 'training'} pageLink="/corporate-training" pageName="Training Programs" />
            </div>
            
            {/* Recruitment Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('recruitment')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
                Recruitment
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'recruitment' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={recruitmentSublinks} isOpen={activeDropdown === 'recruitment'} pageLink="/recruitment" pageName="Recruitment Services" />
            </div>
            
            {/* Software & IT Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('software')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
                Software & IT
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'software' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'software' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72">
                  <div className="bg-[#111827] border border-[#00E5FF]/30 rounded-xl shadow-2xl shadow-[#00E5FF]/20 overflow-hidden">
                    <div className="p-2">
                      {softwareSublinks.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={index}
                            to={item.link}
                            className="flex items-start p-3 rounded-lg hover:bg-[#00E5FF]/10 transition-colors group"
                          >
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00E5FF]/20 to-[#1DE9B6]/20 flex items-center justify-center mr-3 group-hover:from-[#00E5FF]/30 group-hover:to-[#1DE9B6]/30 transition-all">
                              <Icon className="w-4 h-4 text-[#00E5FF]" />
                            </div>
                            <div>
                              <div className="text-white font-medium text-sm group-hover:text-[#00E5FF] transition-colors">{item.name}</div>
                              <div className="text-[#9CA3AF] text-xs">{item.desc}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="border-t border-[#00E5FF]/20 p-3 bg-[#0B0F1A]/50">
                      <div className="flex gap-2">
                        <Link to="/software-development" className="flex-1 text-center text-xs py-2 px-3 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-colors">
                          Software Dev →
                        </Link>
                        <Link to="/it-services" className="flex-1 text-center text-xs py-2 px-3 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-colors">
                          IT Services →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Certifications Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown('certifications')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
                Certifications
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'certifications' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={certificationSublinks} isOpen={activeDropdown === 'certifications'} pageLink="/certifications/framework" pageName="Certifications" />
            </div>

            <button onClick={() => scrollToSection('footer')} className="text-[#9CA3AF] hover:text-[#00E5FF] font-medium transition-colors text-sm">
              Contact
            </button>

            <Link to="/book-a-consultation" className="text-[#1DE9B6] hover:text-[#00E5FF] font-medium transition-colors text-sm">
              Book a Free Session
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden p-2 rounded-lg hover:bg-[#111827] transition-colors text-[#00E5FF]"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-[#00E5FF]/20 bg-[#111827]/50 backdrop-blur-lg max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-left text-[#9CA3AF] hover:text-[#00E5FF] font-medium">
                Home
              </Link>
              <button onClick={() => scrollToSection('why-choose')} className="text-left text-[#9CA3AF] hover:text-[#00E5FF] font-medium">
                About Us
              </button>
              
              {/* Mobile Services Submenu */}
              <div className="space-y-2">
                <div className="text-[#00E5FF] font-medium">Services</div>
                <div className="pl-4 space-y-2 border-l-2 border-[#00E5FF]/30">
                  {servicesSublinks.map((item, index) => (
                    <Link key={index} to={item.link} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-[#9CA3AF] hover:text-[#00E5FF]">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Mobile Training Submenu */}
              <div className="space-y-2">
                <div className="text-[#00E5FF] font-medium">Training & Placement</div>
                <div className="pl-4 space-y-2 border-l-2 border-[#00E5FF]/30">
                  {trainingSublinks.map((item, index) => (
                    <Link key={index} to={item.link} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-[#9CA3AF] hover:text-[#00E5FF]">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Mobile Recruitment Submenu */}
              <div className="space-y-2">
                <div className="text-[#00E5FF] font-medium">Recruitment</div>
                <div className="pl-4 space-y-2 border-l-2 border-[#00E5FF]/30">
                  {recruitmentSublinks.map((item, index) => (
                    <Link key={index} to={item.link} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-[#9CA3AF] hover:text-[#00E5FF]">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Mobile Software & IT Submenu */}
              <div className="space-y-2">
                <div className="text-[#00E5FF] font-medium">Software & IT</div>
                <div className="pl-4 space-y-2 border-l-2 border-[#00E5FF]/30">
                  {softwareSublinks.map((item, index) => (
                    <Link key={index} to={item.link} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-[#9CA3AF] hover:text-[#00E5FF]">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Mobile Certifications Submenu */}
              <div className="space-y-2">
                <div className="text-[#00E5FF] font-medium">Certifications</div>
                <div className="pl-4 space-y-2 border-l-2 border-[#00E5FF]/30">
                  {certificationSublinks.map((item, index) => (
                    <Link key={index} to={item.link} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-[#9CA3AF] hover:text-[#00E5FF]">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              <button onClick={() => scrollToSection('footer')} className="text-left text-[#9CA3AF] hover:text-[#00E5FF] font-medium">
                Contact
              </button>
              <Link to="/book-a-consultation" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1DE9B6] hover:text-[#00E5FF] font-medium">
                Book a Free Session
              </Link>
              <Button
                onClick={() => scrollToSection('career-consultation')}
                className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold w-full"
              >
                Free Consultation
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
